import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';

/** Figma 3779:5953 presets. Width and hero ratio scale together on narrow viewports. */
export const feedbackShellPreset = {
  contentWidth: 393,
  gutter: 16,
  overlap: 44,
  heroAspectRatio: 393 / 497,
  headerHeight: 64,
  cardRadius: 18,
  actionHeight: 48,
} as const;

export interface FeedbackSurveyShellProps {
  children: ReactNode;
  header?: ReactNode;
  hero?: ReactNode;
  mediaControls?: ReactNode;
  footer?: ReactNode;
  direction?: 'ltr' | 'rtl';
  layout?: Partial<{
    contentWidth: number;
    gutter: number;
    overlap: number;
    heroAspectRatio: number;
    headerHeight: number;
    minimumMediaHeight: number;
    cardRadius: number;
  }>;
}

/** Presentation only: navigation, payment eligibility and persistence belong to the runtime. */
export function FeedbackSurveyShell({
  children,
  header,
  hero,
  mediaControls,
  footer,
  direction = 'ltr',
  layout,
}: FeedbackSurveyShellProps) {
  const geometry = { ...feedbackShellPreset, ...layout };
  const hasHero = hero !== undefined && hero !== null;
  return (
    <Box
      dir={direction}
      data-survey-presentation="feedback_2026"
      sx={{
        minHeight: '100dvh',
        width: '100%',
        bgcolor: 'var(--cx-feedback-background, #F9FAFB)',
        color: 'var(--cx-brand-text, #1C252E)',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: geometry.contentWidth,
          mx: 'auto',
          position: 'relative',
          minHeight: '100dvh',
        }}
      >
        {header && (
          <Box
            data-testid="feedback-header"
            sx={{
              position: 'relative',
              minHeight: geometry.headerHeight,
              px: `${geometry.gutter}px`,
              py: '14px',
              zIndex: 2,
              background: 'var(--cx-feedback-header, #FFFFFF)',
            }}
          >
            {header}
          </Box>
        )}
        {hasHero && (
          <Box
            data-testid="feedback-hero"
            sx={{
              aspectRatio: geometry.heroAspectRatio,
              minHeight: geometry.minimumMediaHeight
                ? geometry.overlap + geometry.minimumMediaHeight
                : undefined,
              width: '100%',
              position: 'relative',
              overflow: 'hidden',
              bgcolor: 'var(--cx-brand-text, #1C252E)',
            }}
          >
            {/* An inset stage gives percentage-height media a definite containing block in WebKit. */}
            <Box
              data-testid="feedback-hero-stage"
              sx={{
                position: 'absolute',
                inset: 0,
                '& > img, & > video': { width: '100%', height: '100%', objectFit: 'cover' },
              }}
            >
              {hero}
            </Box>
          </Box>
        )}
        <Box
          data-testid="feedback-content"
          sx={{
            position: 'relative',
            zIndex: 1,
            mt: hasHero ? `-${geometry.overlap}px` : 0,
            pt: !hasHero && header ? '24px' : 0,
            px: `${geometry.gutter}px`,
            pb: 3,
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {mediaControls}
          {children}
        </Box>
        {footer}
      </Box>
    </Box>
  );
}

export function FeedbackSurveyCard({
  children,
  radius = feedbackShellPreset.cardRadius,
}: {
  children: ReactNode;
  radius?: number;
}) {
  return (
    <Card
      data-feedback-card
      sx={{
        p: 2,
        borderRadius: `${radius}px`,
        bgcolor: 'var(--cx-feedback-card, rgba(255,255,255,0.75))',
        color: 'inherit',
        // Figma's GLASS effect uses a 52px frost radius. Browsers do not expose
        // its refraction controls, so backdrop blur plus edge light is the
        // closest stable CSS treatment for the authored surface.
        backdropFilter: 'blur(52px)',
        WebkitBackdropFilter: 'blur(52px)',
        boxShadow:
          '0 8px 16px rgba(145,158,171,0.16), inset 0 1px 0 rgba(255,255,255,0.28), inset 0 -1px 0 rgba(255,255,255,0.08)',
        overflow: 'visible',
        minWidth: 0,
      }}
    >
      {children}
    </Card>
  );
}
